import DOMPurify from 'dompurify';
import { diffWordsWithSpace } from 'diff';

import { ResourceVersionListItem } from '@/types';

export type MemoDiffChangeType = 'added' | 'removed' | 'replaced';

export interface MemoDiffChange {
    after: string;
    before: string;
    id: string;
    type: MemoDiffChangeType;
}

export interface MemoDiffResult {
    changes: MemoDiffChange[];
    html: string;
}

interface AddedRange {
    changeId: string;
    end: number;
    start: number;
}

interface RemovedRange {
    changeId: string;
    offset: number;
    text: string;
}

interface TextNodeRange {
    end: number;
    node: Text;
    start: number;
}

const getBody = (value: unknown): string => (typeof value === 'string' ? value : '');

const createDiffSpan = (
    document: Document,
    changeId: string,
    type: 'added' | 'removed',
    value: string,
): HTMLSpanElement => {
    const span = document.createElement('span');
    span.dataset.changeId = changeId;
    span.dataset.memoDiff = type;
    span.textContent = value;
    return span;
};

const collectTextNodeRanges = (document: Document): TextNodeRange[] => {
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
    const ranges: TextNodeRange[] = [];
    let offset = 0;
    let node = walker.nextNode();

    while (node) {
        const textNode = node as Text;
        const length = textNode.data.length;

        if (length > 0) {
            ranges.push({ end: offset + length, node: textNode, start: offset });
            offset += length;
        }

        node = walker.nextNode();
    }

    return ranges;
};

const getOwningRange = (ranges: TextNodeRange[], offset: number): TextNodeRange | undefined => {
    return ranges.find((range) => offset >= range.start && offset < range.end) ?? ranges.at(-1);
};

const annotateTextNode = (
    document: Document,
    range: TextNodeRange,
    additions: AddedRange[],
    removals: RemovedRange[],
): void => {
    const relevantAdditions = additions.filter((addition) => addition.start < range.end && addition.end > range.start);
    const relevantRemovals = removals.filter((removal) => getOwningRange([range], removal.offset) === range);

    if (!relevantAdditions.length && !relevantRemovals.length) {
        return;
    }

    const boundaries = new Set<number>([0, range.node.data.length]);
    const removalsByOffset = new Map<number, RemovedRange[]>();

    relevantAdditions.forEach((addition) => {
        boundaries.add(Math.max(0, addition.start - range.start));
        boundaries.add(Math.min(range.node.data.length, addition.end - range.start));
    });

    relevantRemovals.forEach((removal) => {
        const localOffset = Math.max(0, Math.min(range.node.data.length, removal.offset - range.start));
        boundaries.add(localOffset);
        removalsByOffset.set(localOffset, [...(removalsByOffset.get(localOffset) ?? []), removal]);
    });

    const orderedBoundaries = Array.from(boundaries).sort((left, right) => left - right);
    const fragment = document.createDocumentFragment();

    orderedBoundaries.forEach((boundary, index) => {
        removalsByOffset.get(boundary)?.forEach((removal) => {
            fragment.append(createDiffSpan(document, removal.changeId, 'removed', removal.text));
        });

        const nextBoundary = orderedBoundaries[index + 1];

        if (nextBoundary === undefined || nextBoundary <= boundary) {
            return;
        }

        const value = range.node.data.slice(boundary, nextBoundary);
        const globalOffset = range.start + boundary;
        const addition = relevantAdditions.find(
            (candidate) => globalOffset >= candidate.start && globalOffset < candidate.end,
        );

        fragment.append(
            addition ? createDiffSpan(document, addition.changeId, 'added', value) : document.createTextNode(value),
        );
    });

    range.node.replaceWith(fragment);
};

const annotateDocument = (document: Document, additions: AddedRange[], removals: RemovedRange[]): void => {
    const ranges = collectTextNodeRanges(document);

    if (!ranges.length) {
        if (removals.length) {
            const paragraph = document.createElement('p');
            removals.forEach((removal) => {
                paragraph.append(createDiffSpan(document, removal.changeId, 'removed', removal.text));
            });
            document.body.append(paragraph);
        }

        return;
    }

    const removalsByRange = new Map<TextNodeRange, RemovedRange[]>();

    removals.forEach((removal) => {
        const owner = getOwningRange(ranges, removal.offset);

        if (owner) {
            removalsByRange.set(owner, [...(removalsByRange.get(owner) ?? []), removal]);
        }
    });

    ranges.forEach((range) => {
        annotateTextNode(document, range, additions, removalsByRange.get(range) ?? []);
    });
};

export const buildMemoDiff = (beforeHtml: string, afterHtml: string): MemoDiffResult => {
    const parser = new DOMParser();
    const beforeDocument = parser.parseFromString(beforeHtml, 'text/html');
    const afterDocument = parser.parseFromString(afterHtml, 'text/html');
    const beforeText = beforeDocument.body.textContent ?? '';
    const afterText = afterDocument.body.textContent ?? '';
    const parts = diffWordsWithSpace(beforeText, afterText);
    const additions: AddedRange[] = [];
    const removals: RemovedRange[] = [];
    const changes: MemoDiffChange[] = [];
    let afterOffset = 0;
    let partIndex = 0;

    while (partIndex < parts.length) {
        const part = parts[partIndex];

        if (!part.added && !part.removed) {
            afterOffset += part.value.length;
            partIndex += 1;
            continue;
        }

        const changeId = `memo-change-${changes.length + 1}`;
        const changeStart = afterOffset;
        let before = '';
        let after = '';

        while (partIndex < parts.length && (parts[partIndex].added || parts[partIndex].removed)) {
            const changedPart = parts[partIndex];

            if (changedPart.removed) {
                before += changedPart.value;
            }

            if (changedPart.added) {
                const start = afterOffset;
                after += changedPart.value;
                afterOffset += changedPart.value.length;
                additions.push({ changeId, end: afterOffset, start });
            }

            partIndex += 1;
        }

        if (before) {
            removals.push({ changeId, offset: changeStart, text: before });
        }

        changes.push({
            after,
            before,
            id: changeId,
            type: before && after ? 'replaced' : before ? 'removed' : 'added',
        });
    }

    annotateDocument(afterDocument, additions, removals);

    return {
        changes,
        html: afterDocument.body.innerHTML,
    };
};

export const buildMemoVersionDiff = (version: ResourceVersionListItem): MemoDiffResult => {
    const bodyChange = version.changes?.find((change) => change.path === 'body' || change.path.startsWith('body.'));
    const afterBody = getBody(bodyChange?.after ?? version.snapshot?.body);
    const beforeBody = getBody(bodyChange ? bodyChange.before : afterBody);

    return buildMemoDiff(DOMPurify.sanitize(beforeBody), DOMPurify.sanitize(afterBody));
};
